import { z } from "zod";
import { AGENT_MEDIA_KINDS, PRODUCT_GALLERY_MAX_FILES } from "./agent-media.js";
/**
 * Rich replies on the AI-first site (docs/plans/ai-first-site.md, Decision 5,
 * phase 5): what the agent SHOWS beside its text, as the widget receives it in
 * the `ui` SSE event and on the history.
 *
 * The model never writes a block's content. It names what to show through a
 * tool; the backend builds the block from the database. That is why nothing here
 * can carry a price: the site's payload never does (`GET /public/sites/:host`),
 * and a card must not be the one place a quotes tenant's agent recites one.
 */
/** At most this many product cards in one reply: a row the visitor can scan. */
export const MAX_SITE_UI_PRODUCTS = 4;
/** A card shows two lines of description; the rest is the product page's. */
export const MAX_SITE_UI_DESCRIPTION_LENGTH = 240;
export const siteUiProductSchema = z.object({
    slug: z.string(),
    name: z.string(),
    categoryName: z.string().nullable(),
    description: z.string().nullable(),
    /** A gallery image id, served by the site at `/media/<id>`. */
    imageId: z.string().nullable(),
});
/**
 * One file of a material the agent showed in the conversation (`send_document` on
 * the site): served by the site at `/media/<id>`, a redirect to a short-lived
 * signed URL, so the file never travels through apps/site.
 */
export const siteUiMediaFileSchema = z.object({
    id: z.string(),
    kind: z.enum(AGENT_MEDIA_KINDS),
    fileName: z.string(),
});
export const siteUiBlockSchema = z.discriminatedUnion("kind", [
    z.object({
        kind: z.literal("products"),
        products: z.array(siteUiProductSchema).min(1).max(MAX_SITE_UI_PRODUCTS),
    }),
    z.object({
        kind: z.literal("media"),
        /** A gallery is several photos; anything else is one file. */
        material: z.enum([...AGENT_MEDIA_KINDS, "gallery"]),
        title: z.string(),
        files: z.array(siteUiMediaFileSchema).min(1).max(PRODUCT_GALLERY_MAX_FILES),
    }),
]);
/**
 * The quote request the agent is assembling with the visitor — «Tu cotización»,
 * drawn above the conversation. The request the agent may still edit
 * (`leads.active_quote_id`): a `draft`, or a `submitted` one nobody has taken.
 * Read-only on the page: every change goes through the agent's quote tools, so
 * quantities are asked and changed in the conversation. No price, as everywhere
 * on the site — a quotes tenant has none to show.
 */
export const siteQuoteLineSchema = z.object({
    position: z.number().int().min(1),
    productName: z.string(),
    reference: z.string().nullable(),
    quantity: z.number().int().min(1),
    /** The product's first gallery photo (`/media/<id>`), when it has one. */
    imageId: z.string().nullable(),
});
export const siteQuoteSchema = z.object({
    number: z.number().int(),
    status: z.enum(["draft", "submitted"]),
    lines: z.array(siteQuoteLineSchema).min(1),
});
//# sourceMappingURL=site-ui.js.map